import React, { useState } from 'react';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import PerspectiveSelector from '../PerspectiveSelector/PerspectiveSelector';
import { Sphere } from 'types/global';
import { useTranslation } from 'localization';
import './InviteUsersMolecule.scss';

type InviteUsersDialogProps = Readonly<{
  visible: boolean;
  onHide: () => void;
  fetchUsers: () => void;
  organizationId: string;
}>;

/** An invitee is named by email alone: the invite resolves who holds it on the server. */
type Invitee = {
  email: string;
};

export default function InviteUsersDialogMolecule({ visible, onHide, organizationId, fetchUsers }: InviteUsersDialogProps) {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<Invitee[]>([]);
  const [center, setCenter] = useState<string | null>(null);
  const [_, setSendWelcome] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiClients = useApiClientsOrNull();
  const organizationClient = apiClients?.organizationClient;

  // Minimal sphere object so PerspectiveSelector can call /search/potential-assignees
  const selectorSphere = {
    id: organizationId,
    label: 'center',
    properties: { type: 'center_user', name: '' },
    assignedPerspectives: [],
  } as unknown as Sphere;

  // The center the invitees join: the one picked, else the organization. The invite is gated on
  // `edit` of it.
  const targetCenter = center || organizationId;

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const email = emailInput.trim();
    if ((e.key === 'Enter' || e.key === ',') && email) {
      e.preventDefault();
      addEmail({ email });
    }
  };

  const addEmail = (invitee: Invitee) => {
    if (!emails.find((e) => e.email === invitee.email)) {
      setEmails([...emails, invitee]);
      setEmailInput('');
    }
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter((e) => e.email !== email));
  };

  if (!apiClients) {
    return null;
  }

  const inviteUserApi = async (email: string, centerId: string) => {
    return organizationClient!.post(`/invite-user`, {
      centerId,
      email,
    });
  };

  const handleInvite = async () => {
    if (emails.length) {
      setLoading(true);
      await Promise.all(
        emails.map((invitee) =>
          inviteUserApi(invitee.email, targetCenter)
            .then((res) => ({ email: invitee.email, ok: res.ok }))
            .catch(() => ({ email: invitee.email, ok: false }))
        )
      );
      setLoading(false);
      fetchUsers();

      setEmails([]);
      setEmailInput('');
      setCenter(null);
      setSendWelcome(false);
      onHide();
    }
  };

  return (
    <DialogWrapper header={t('inviteUserDialog.inviteUser')} visible={visible} onHide={onHide} className="invite-users-dialog">
      <div className="invite-users-dialog-content">
        <div className="invite-users-dialog-intro">{t('inviteUserDialog.inviteUserDialogDescription')}</div>

        {/* Email Addresses Input */}
        <div className="mb-16">
          <div className="invite-users-label">{t('inviteUserDialog.inviteUserEmailLabel')}</div>
          <div className="invite-emails-input">
            {emails.map((invitee) => (
              <span key={invitee.email} className="invite-email-tag" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{invitee.email}</span>
                <button className="remove-tag" type="button" onClick={() => removeEmail(invitee.email)} aria-label={t('inviteUserDialog.remove')}>
                  &times;
                </button>
              </span>
            ))}
            <input
              className="invite-email-input"
              placeholder={t('inviteUserDialog.inviteUserEmailPlaceholder')}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
            />
          </div>
          <div className="invite-users-helper">{t('inviteUserDialog.inviteUserHelper')}</div>
        </div>

        {/* Center Perspective Selector */}
        <div className="mb-20 invite-users-label">{t('inviteUserDialog.center')}</div>
        <PerspectiveSelector
          className="user-centers"
          perspectiveSystemName="center_center"
          sphere={selectorSphere}
          organizationId={organizationId}
          stayOpen
          lazy={false}
          onAssigneeSelected={(assignee) => {
            setCenter(typeof assignee === 'string' ? assignee : null);
          }}
        />

        {/* Buttons */}
        <div className="invite-users-actions">
          <ButtonAtom type="button" variant="secondary" label={t('common.actions.cancel', 'Cancel')} onClick={onHide} />
          <ButtonAtom
            type="button"
            variant="primary"
            label={loading ? t('inviteUserDialog.inviteUserLoading', 'Inviting...') : t('inviteUserDialog.inviteUser')}
            onClick={handleInvite}
            disabled={!emails.length || loading}
          />
        </div>
      </div>
    </DialogWrapper>
  );
}
